import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronDown, ShieldCheck, Timer, Activity,
  Radar, Layers, Sparkles, ClipboardCheck } from "lucide-react";
import Shell from "@/components/Shell";
import Brandmark from "@/components/Brandmark";
import { useApp } from "@/lib/store";
import { track } from "@/lib/analytics";
import { FAQ_ITEMS } from "@/pages/LegalPage";
import { SCOREAPP_SNAPSHOT_URL } from "@/lib/config";
import { setAttribution } from "@/lib/attribution";

const TOP_FAQ = [1, 2, 6];

const STAGES = [
  { icon: ClipboardCheck, title: "Check-in", body: "Answer three questions in about 60 seconds." },
  { icon: Activity, title: "History", body: "Your signals build over time." },
  { icon: Layers, title: "Patterns", body: "Meaningful relationships begin appearing in your data." },
  { icon: Radar, title: "Drift Radar", body: "Early signs of change become easier to notice." },
  { icon: Sparkles, title: "Earned Intelligence", body: "Insights sharpen as Signal learns your patterns." },
];

const TRUST = [
  { icon: ShieldCheck, label: "Private by default" },
  { icon: Timer, label: "About 60 seconds" },
  { icon: Activity, label: "No streak pressure" },
];

export default function Mission() {
  const nav = useNavigate();
  const { user, authReady } = useApp();
  const [showFaq, setShowFaq] = useState(false);

  useEffect(() => {
    if (!authReady) return;
    const seen = localStorage.getItem("ss_seen_mission") === "true";
    if (user || seen) nav("/intro", { replace: true });
    else track("landing_view");
  }, [authReady, user, nav]);

  const proceed = () => {
    localStorage.setItem("ss_seen_mission", "true");
    nav("/intro");
  };
  const openSnapshot = () => {
    setAttribution({ entry_context: "direct" });
    track("scoreapp_click", { source: "landing" });
    window.open(SCOREAPP_SNAPSHOT_URL, "_blank", "noopener");
  };

  return (
    <Shell className="ss-dark">
      <div className="min-h-dvh px-6 pt-16 pb-16">
        {/* ambient backlight */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-72 opacity-60"
          style={{ background: "radial-gradient(120% 60% at 50% 0%, rgba(0,229,160,0.10), rgba(56,189,248,0.05) 40%, transparent 70%)" }} />

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} className="relative">
          <Brandmark size={34} color="#00E5A0" />
        </motion.div>

        {/* Hero */}
        <div className="relative mt-14">
          <motion.h1
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="text-4xl sm:text-5xl font-bold tracking-tight leading-[1.08] text-slate-50"
            data-testid="hero-headline"
          >
            MOST PEOPLE<br />DON'T CRASH.<br />
            <span className="text-[#00E5A0]">THEY DRIFT.</span>
          </motion.h1>
          <p className="mt-6 text-[15px] leading-relaxed text-slate-300">
            Signal State ID helps you notice changes in how you're operating before they become
            visible decline — and gives you one useful next move in about 60 seconds.
          </p>
        </div>

        {/* Primary CTA */}
        <motion.div
          className="relative mt-8"
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
        >
          <button
            data-testid="mission-continue"
            onClick={proceed}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-6 py-4 text-base font-semibold ss-tap ss-focus"
            style={{ backgroundColor: "#00E5A0", color: "#0B0F17", boxShadow: "0 0 28px rgba(0,229,160,0.28)" }}
          >
            Check My Signal State <ArrowRight size={18} strokeWidth={2} />
          </button>
          <p className="mt-3 text-center text-[12.5px] text-slate-400">
            3 questions. About 60 seconds. Private by default.
          </p>
          <button
            data-testid="mission-snapshot"
            onClick={openSnapshot}
            className="mt-3 w-full text-center text-[13px] font-medium text-[#38BDF8] hover:text-[#7dd3fc] ss-focus transition-colors"
          >
            Or take the Signal State Snapshot →
          </button>
        </motion.div>

        {/* Trust row */}
        <div className="relative mt-8 grid grid-cols-3 gap-2">
          {TRUST.map((t) => (
            <div key={t.label} className="flex flex-col items-center gap-1.5 rounded-xl border border-[#222D3D] bg-[#131A26] px-2 py-3 text-center">
              <t.icon size={16} strokeWidth={1.75} className="text-[#00E5A0]" />
              <span className="text-[10.5px] leading-tight text-slate-400">{t.label}</span>
            </div>
          ))}
        </div>

        {/* Positioning statement */}
        <div className="relative mt-14">
          <p className="text-[19px] sm:text-xl font-medium leading-snug text-slate-100">
            Signal State ID isn't here to tell you what decision to make.{" "}
            <span className="text-slate-400">It's here to help you understand who is making it.</span>
          </p>
        </div>

        {/* How it works */}
        <section className="relative mt-14">
          <span className="font-mono-ss text-[11px] uppercase tracking-[0.2em] text-[#00E5A0]">How it works</span>
          <h2 className="mt-2 text-xl sm:text-2xl font-semibold tracking-tight text-slate-100 leading-snug">
            One check-in helps now. Repeated signals reveal something more valuable.
          </h2>
          <div className="mt-6 space-y-3">
            {STAGES.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }} transition={{ delay: 0.04 * i }}
                className="flex items-start gap-4 rounded-2xl border border-[#222D3D] bg-[#131A26] p-4"
                data-testid={`stage-${i}`}
              >
                <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl bg-[#1A2332] border border-[#222D3D]">
                  <s.icon size={18} strokeWidth={1.75} className="text-[#38BDF8]" />
                </div>
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-mono-ss text-[11px] text-[#00E5A0]">{String(i + 1).padStart(2, "0")}</span>
                    <h3 className="text-[15.5px] font-medium text-slate-100">{s.title}</h3>
                  </div>
                  <p className="mt-1 text-[13.5px] leading-relaxed text-slate-400">{s.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Common questions (collapsed) */}
        <div className="relative mt-12" data-testid="mission-faq-preview">
          <button
            type="button"
            onClick={() => setShowFaq((v) => !v)}
            data-testid="mission-faq-toggle"
            aria-expanded={showFaq}
            className="mx-auto flex items-center justify-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#38BDF8] ss-focus"
          >
            Common questions
            <ChevronDown size={14} strokeWidth={2} className={`transition-transform ${showFaq ? "rotate-180" : ""}`} />
          </button>
          {showFaq && (
            <div className="mt-3 flex flex-col gap-2">
              {TOP_FAQ.map((idx) => (
                <details key={idx} className="ss-mfaq group rounded-xl border border-[#222D3D] bg-[#131A26] px-4 py-3" data-testid={`mission-faq-item-${idx}`}>
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-2 text-[13.5px] font-medium text-slate-100 ss-focus">
                    {FAQ_ITEMS[idx].q}
                    <ChevronDown size={16} strokeWidth={1.75} className="shrink-0 text-[#38BDF8] transition-transform group-open:rotate-180" />
                  </summary>
                  <p className="mt-2 text-[13px] leading-relaxed text-slate-400">{FAQ_ITEMS[idx].a}</p>
                </details>
              ))}
              <button onClick={() => nav("/faq")} data-testid="mission-faq-all" className="mt-1 block w-full text-center text-[12.5px] text-[#38BDF8] underline underline-offset-2 ss-focus">
                See all questions
              </button>
            </div>
          )}
          <style>{`.ss-mfaq summary::-webkit-details-marker{display:none}`}</style>
        </div>

        <div className="relative mt-10 flex items-center justify-center gap-3 text-[11.5px] text-slate-500">
          <button data-testid="mission-privacy" onClick={() => nav("/privacy")} className="hover:text-slate-300 ss-focus underline underline-offset-2">Privacy</button>
          <span>·</span>
          <button data-testid="mission-terms" onClick={() => nav("/terms")} className="hover:text-slate-300 ss-focus underline underline-offset-2">Terms</button>
        </div>
      </div>
    </Shell>
  );
}
